import { Box, LinearProgress, Skeleton, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import "./translations";

import { ImmoScoutIcon, LineChartUpIcon, ShieldCheckIcon } from "@/assets";
import { PropertyAddIcon } from "@/assets/icons/PropertyAddIcon";
import { CustomChip } from "@/components/customChip/CustomChip";
import { demandScoreTranslationKeys } from "@/components/demandScorePanel/translations";
import { EnhancedTooltip } from "@/components/enhancedTooltip";
import { PanelHeader } from "@/components/panelHeader";
import { getAvmDisplayedRange } from "@/helpers/avm";
import { getProsaWebMarketValue } from "@/helpers/prosaWeb";
import { getObjectTypeLabelWIthBuildingType } from "@/helpers/realEstateParametersHelpers";
import { useModuleContext } from "@/hooks/useModuleContext";
import { CurrentValuationState } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { ValuationReportState } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";
import { colors } from "@/theme/tokens/colors";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

import { ObjectDetails } from "./ObjectDetails";
import { PriceCard } from "./PriceCard";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panelContainer: {
    display: "grid",
    backgroundColor: "white",
    padding: theme.analysisLayout.panelPadding,
    gap: theme.analysisLayout.panelGap,
  },
  headerSection: {
    display: "grid",
    gap: theme.analysisLayout.compactGap,
  },
  priceGrid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(3, 1fr)",
      lg: "repeat(3, 1fr)",
    },
    gap: theme.analysisLayout.sectionGap,
  },
  card: {
    display: "flex",
    alignItems: "start",
    gap: theme.analysisLayout.cardGap,
    flexDirection: "column",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "4px",
    padding: theme.analysisLayout.cardPaddingCompact,
  },
  headerIcon: {
    fontSize: theme.analysisLayout.headerIconSize,
    color: colors.brand.brownDark,
  },
  priceText: {
    height: "40px",
    alignContent: "center",
  },
  demandScoreValue: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
    height: "40px",
  },
  demandScoreProgressContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "21px",
  },
  demandScoreProgress: {
    width: "100%",
    height: "8px",
  },
  unavailablePriceRangeText: {
    fontSize: "12px",
    lineHeight: "20px",
  },
  priceCardSource: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  priceCardSourceIcon: {
    color: "success.dark",
    fontSize: "18px",
  },
  priceCardSourceLabel: {
    fontSize: "13px",
  },
});

interface PropertyDetailsPanelProps {
  valuationState: ValuationReportState | CurrentValuationState;
  isValuationPackageWidget?: boolean;
  isLoading?: boolean;
}

export const PropertyDetailsPanel = ({
  valuationState,
  isValuationPackageWidget,
  isLoading = false,
}: PropertyDetailsPanelProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("PropertyDetailsPanel");
  const { t: tDemandScore } = useTranslation("DemandScorePanel");
  const { isValuationReport } = useModuleContext();
  const { t: tCommon } = useTranslation();
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });
  const { avm, demandScore, prosaWeb } = valuationState;
  const objectType = valuationState.propertyInformation?.generalInfo.objectType;
  const buildingType = valuationState.propertyInformation?.generalInfo.buildingType;
  const isPlot = objectType === ObjectTypeEnum.plotOfLand;
  const showMarketDemand =
    !isPlot && objectType !== ObjectTypeEnum.multiFamilyHouse && objectType !== ObjectTypeEnum.mixedUseProperty;
  const objectTypeLabel = objectType
    ? getObjectTypeLabelWIthBuildingType(objectType, tRealEstate, buildingType)
    : undefined;
  const marketValue = getProsaWebMarketValue(prosaWeb, {
    useRoundedValue: isValuationReport,
    objectType: valuationState.propertyInformation?.generalInfo.objectType,
    livingArea: valuationState.propertyInformation?.buildingData.livingArea,
  });
  const avmDisplayedRange = getAvmDisplayedRange(
    avm,
    "selectedEquipmentPriceRange" in valuationState ? valuationState.selectedEquipmentPriceRange : undefined,
  );

  const livingArea = valuationState.propertyInformation?.buildingData?.livingArea;
  const calculatePricePerSqm = (price?: number | null): number | undefined =>
    price && livingArea ? Math.round(price / livingArea) : undefined;
  const formatPrice = (price?: number | null): string => (price ? `${price.toLocaleString("de-DE")} €` : "");
  const formatPricePerSqm = (price?: number | null): string => {
    const pricePerSqm = calculatePricePerSqm(price);
    return pricePerSqm ? `${pricePerSqm.toLocaleString("de-DE")} €/m²` : " ";
  };
  const priceRangePerSquareMeterMin = calculatePricePerSqm(avmDisplayedRange.min);
  const priceRangePerSquareMeterMax = calculatePricePerSqm(avmDisplayedRange.max);
  const marketPriceLabel = formatPrice(marketValue);
  const hasMarketValue = Boolean(marketValue);
  const hasMarketPriceRange = Boolean(avmDisplayedRange.min && avmDisplayedRange.max);
  const priceRangeLabel = hasMarketPriceRange
    ? `${formatPrice(avmDisplayedRange.min).replace(" €", "")} - ${formatPrice(avmDisplayedRange.max)}`
    : "";
  const marketPricePerSquareMeterLabel = formatPricePerSqm(marketValue);
  const priceRangePerSquareMeterLabel =
    priceRangePerSquareMeterMin && priceRangePerSquareMeterMax
      ? `${priceRangePerSquareMeterMin.toLocaleString("de-DE")} - ${priceRangePerSquareMeterMax.toLocaleString("de-DE")} €/m²`
      : " ";
  const demandScoreClass = demandScore?.demand_score_class;
  const demandScoreTranslationKey = demandScoreClass ? demandScoreTranslationKeys[demandScoreClass] : undefined;
  const demandScoreValue = demandScore?.value;
  const demandScoreText = demandScoreTranslationKey ? tDemandScore(`${demandScoreTranslationKey}.text`) : "";
  const demandScoreLabel = demandScoreText
    ? `${demandScoreText.charAt(0).toLocaleUpperCase()}${demandScoreText.slice(1)}`
    : "";
  const hasDemandScore = demandScoreValue !== undefined && Boolean(demandScoreLabel);

  const livingAreaLabel = tCommon("realEstate.livingArea");

  return (
    <Box sx={styles.panelContainer} data-cy="property-details-panel">
      <Box sx={styles.headerSection}>
        <PanelHeader
          title={t("title")}
          icon={<PropertyAddIcon sx={styles.headerIcon} />}
          rightContent={
            objectTypeLabel ? (
              <CustomChip
                label={objectTypeLabel}
                chipType="default"
                fontSize={theme.analysisLayout.customChipFontSize}
                sx={{ backgroundColor: "background.paper" }}
              />
            ) : undefined
          }
        />

        <Box
          sx={{
            ...styles.priceGrid,
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${showMarketDemand ? 3 : 2}, 1fr)`,
              lg: `repeat(${showMarketDemand ? 3 : 2}, 1fr)`,
            },
          }}
        >
          <PriceCard
            title={t("substantiatedMarketValue")}
            hint={isPlot || !hasMarketValue ? "" : `${marketPricePerSquareMeterLabel} ${livingAreaLabel}`}
            isHintLoading={!isPlot && isLoading}
            titleAdornment={
              <CustomChip
                label={t("marketValueComplianceLabel")}
                chipType="primary"
                fontSize={theme.analysisLayout.customChipFontSize}
                sx={{ backgroundColor: "background.paper" }}
                tooltip={{ content: t("marketValueComplianceTooltip") }}
              />
            }
            footer={
              <EnhancedTooltip content={t("marketValueSourceTooltip")}>
                <Box sx={styles.priceCardSource}>
                  <ShieldCheckIcon sx={styles.priceCardSourceIcon} />
                  <Typography variant="hintText" sx={styles.priceCardSourceLabel}>
                    {t("marketValueSource")}
                  </Typography>
                </Box>
              </EnhancedTooltip>
            }
            sx={styles.card}
          >
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={140} height={32} />
            ) : (
              <Typography
                variant="modalHeader"
                sx={isValuationPackageWidget && !hasMarketValue ? styles.unavailablePriceRangeText : styles.priceText}
                data-cy="property-details-market-price-value"
              >
                {isValuationPackageWidget && !hasMarketValue ? t("marketValueUnavailable") : marketPriceLabel}
              </Typography>
            )}
          </PriceCard>
          <PriceCard
            title={t("currentMarketPositioning")}
            hint={isPlot || !hasMarketPriceRange ? "" : `${priceRangePerSquareMeterLabel} ${livingAreaLabel}`}
            isHintLoading={!isPlot && isLoading}
            footer={
              <EnhancedTooltip content={t("marketPriceRangeSourceTooltip")}>
                <Box sx={styles.priceCardSource}>
                  <LineChartUpIcon sx={styles.priceCardSourceIcon} />
                  <Typography variant="hintText" sx={styles.priceCardSourceLabel}>
                    {t("marketPriceRangeSource")}
                  </Typography>
                </Box>
              </EnhancedTooltip>
            }
            sx={styles.card}
          >
            {isLoading ? (
              <Skeleton animation="wave" variant="rounded" width={180} height={32} />
            ) : (
              <Typography
                variant="modalHeader"
                sx={
                  isValuationPackageWidget && !hasMarketPriceRange ? styles.unavailablePriceRangeText : styles.priceText
                }
                data-cy="property-details-market-price-range-value"
              >
                {isValuationPackageWidget && !hasMarketPriceRange ? t("marketPriceRangeUnavailable") : priceRangeLabel}
              </Typography>
            )}
          </PriceCard>
          {showMarketDemand && (
            <PriceCard
              title={t("marketDemand")}
              hintContent={
                hasDemandScore ? (
                  <Box sx={styles.demandScoreProgressContainer}>
                    <LinearProgress
                      variant="determinate"
                      value={demandScoreValue}
                      color="primary"
                      sx={styles.demandScoreProgress}
                    />
                  </Box>
                ) : undefined
              }
              isHintLoading={isLoading}
              footer={
                <EnhancedTooltip content={t("marketDemandSourceTooltip")}>
                  <Box sx={styles.priceCardSource}>
                    <ImmoScoutIcon sx={{ width: "80px" }} />
                    <Typography variant="hintText" sx={styles.priceCardSourceLabel}>
                      {t("marketDemandSource")}
                    </Typography>
                  </Box>
                </EnhancedTooltip>
              }
              sx={styles.card}
            >
              {isLoading ? (
                <Skeleton animation="wave" variant="rounded" width={140} height={32} />
              ) : hasDemandScore ? (
                <Box sx={styles.demandScoreValue} data-cy="property-details-demand-score-value">
                  <Typography variant="modalHeader">{demandScoreValue}</Typography>
                  <Typography variant="hintText">/ 100 · {demandScoreLabel}</Typography>
                </Box>
              ) : (
                <Box sx={{ height: "40px" }} />
              )}
            </PriceCard>
          )}
        </Box>
      </Box>
      <ObjectDetails valuationState={valuationState} isValuationPackageWidget={isValuationPackageWidget} />
    </Box>
  );
};
